import { IProFormaSourceTableRow, ISourceType } from 'views/ProFormaTable/types';

/**
 * Check if a source type is a debt source based on its category.
 * @param sourceTypeId - The correlation ID of the source type
 * @param sourceTypes - Array of source types to search
 * @returns true if the source type category is 'debt'
 */
export const isDebtSource = (sourceTypeId: string | undefined, sourceTypes: ISourceType[] | undefined): boolean => {
    if (!sourceTypeId || !sourceTypes) return false;
    const sourceType = sourceTypes.find(st => st.correlationId === sourceTypeId);
    return sourceType?.sourceTypeCategory === 'debt';
};

/**
 * Check if a source type is an equity source based on its category.
 * @param sourceTypeId - The correlation ID of the source type
 * @param sourceTypes - Array of source types to search
 * @returns true if the source type category is 'equity'
 */
export const isEquitySource = (sourceTypeId: string | undefined, sourceTypes: ISourceType[] | undefined): boolean => {
    if (!sourceTypeId || !sourceTypes) return false;
    const sourceType = sourceTypes.find(st => st.correlationId === sourceTypeId);
    return sourceType?.sourceTypeCategory === 'equity';
};

/**
 * Check if a source type is a builder deposit source based on its category.
 * @param sourceTypeId - The correlation ID of the source type
 * @param sourceTypes - Array of source types to search
 * @returns true if the source type category is 'builder_deposit'
 */
export const isBuilderDepositSource = (sourceTypeId: string | undefined, sourceTypes: ISourceType[] | undefined): boolean => {
    if (!sourceTypeId || !sourceTypes) return false;
    const sourceType = sourceTypes.find(st => st.correlationId === sourceTypeId);
    return sourceType?.sourceTypeCategory === 'builder_deposit';
};

/**
 * Check if a source type is a grant source based on its category.
 * @param sourceTypeId - The correlation ID of the source type
 * @param sourceTypes - Array of source types to search
 * @returns true if the source type category is 'grant'
 */
export const isGrantSource = (sourceTypeId: string | undefined, sourceTypes: ISourceType[] | undefined): boolean => {
    if (!sourceTypeId || !sourceTypes) return false;
    const sourceType = sourceTypes.find(st => st.correlationId === sourceTypeId);
    return sourceType?.sourceTypeCategory === 'grant';
};

/**
 * Check if a source type is an "other" source based on its category.
 * @param sourceTypeId - The correlation ID of the source type
 * @param sourceTypes - Array of source types to search
 * @returns true if the source type category is 'other'
 */
export const isOtherSource = (sourceTypeId: string | undefined, sourceTypes: ISourceType[] | undefined): boolean => {
    if (!sourceTypeId || !sourceTypes) return false;
    const sourceType = sourceTypes.find(st => st.correlationId === sourceTypeId);
    return sourceType?.sourceTypeCategory === 'other';
};

/**
 * Resolve the label for the non-equity/non-debt source draws row based on which
 * source category is present on the deal: Builder Deposit takes priority, then a
 * single unambiguous Grant or Other, otherwise falls back to "Non-equity source".
 * Returns null when no non-equity/non-debt source is present at all.
 * @param sources - The deal's source rows
 * @param sourceTypes - Array of source types to search
 */
export const getNonEquityNonDebtSourceLabel = (
    sources: IProFormaSourceTableRow[] | undefined,
    sourceTypes: ISourceType[] | undefined,
): string | null => {
    const rows = sources ?? [];
    if (rows.some(source => isBuilderDepositSource(source.sourceTypeId, sourceTypes))) {
        return 'Builder deposit';
    }
    const hasGrant = rows.some(source => isGrantSource(source.sourceTypeId, sourceTypes));
    const hasOther = rows.some(source => isOtherSource(source.sourceTypeId, sourceTypes));
    if (hasGrant && !hasOther) return 'Grant';
    if (hasOther && !hasGrant) return 'Other';
    if (hasGrant && hasOther) return 'Non-equity source';
    return null;
};
